// eslint-disable-next-line @altermaind/you-shall-not-pass
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBreakingPoints } from '@/_shared/helpers/useBreakpoint';
import type { MediaController as MediaControllerType } from 'media-chrome';

type MediaControllerElement = MediaControllerType & {
  mediaFullscreenElement?: MediaControllerType['fullscreenElement'];
};

type UsePlayerProps = {
  startFrom?: number;
  onCloseVideo: () => void;
  onVideoEnd?: () => void;
  onVideoProgress?: (activeSecond: number) => void;
  progressSecondInterval?: number;
  onCancelCloseVideoReady?: (handleCancelExit: () => void) => void;
  resetOnEnd?: boolean;
};

/**
 * Hook that manages video playback across Desktop and Mobile/Tablet, including fullscreen behavior,
 * start position, progress sampling, and close/end flows.
 *
 * ## Why Desktop vs Mobile/Tablet
 *
 * The implementation uses two different strategies to ensure consistent behavior across platforms:
 *
 * - **Desktop**: Uses `media-chrome` (MediaController) + `ReactPlayer` for custom controls and
 *   reliable fullscreen via the standard Fullscreen API.
 *
 * - **Mobile/Tablet (iOS/Android)**: Uses the native `<video>` element and browser controls.
 *   This is necessary because:
 *   - iOS Safari does not reliably support the standard Fullscreen API for generic elements
 *   - iOS requires calling `webkitEnterFullscreen` when available
 *   - ReactPlayer can be unreliable on iOS Safari with HLS streams
 *
 * ## Desired flow
 *
 * 1. **Open**
 *    - Desktop: requests fullscreen on mount
 *    - Mobile/Tablet: requests fullscreen on `onLoadedMetadata` (video ready)
 *
 * 2. **Playback**
 *    - Mobile/Tablet: after entering fullscreen, attempts autoplay (`video.play()`)
 *    - Desktop: start position is applied on ReactPlayer ready
 *
 * 3. **Close**
 *    - Pauses playback
 *    - If currently in fullscreen (desktop), exits fullscreen and then calls `onCloseVideo`
 *    - Remembers whether the player was in fullscreen to support restoring fullscreen on cancel
 *
 * 4. **End of video**
 *    - If in fullscreen (desktop), exits fullscreen first
 *    - Calls `onVideoEnd` afterward
 *
 * ## Progress sampling
 *
 * `onVideoProgress` is invoked at most every `progressSecondInterval` seconds (default: 10),
 * based on whole-second time (`Math.floor(currentTime)`).
 *
 * - If the user seeks backward or the time resets, the hook *realigns* immediately and emits
 *   the new second once, without "backfilling" intermediate seconds.
 *
 * ## Notes / caveats
 *
 * - **Mobile Safari**: fullscreen can only be requested after a minimal preload, usually after
 *   `onLoadedMetadata`. Calling it earlier may fail silently.
 * - **Desktop fullscreen exit**: uses `document.exitFullscreen()` instead of the MediaController
 *   exit method for reliability.
 * - The `onProgress` handler supports two call shapes because some players (e.g. ReactPlayer)
 *   may call it with a `number` (seconds) or with a native `<video>` event:
 *   `number | SyntheticEvent<HTMLVideoElement, Event>`.
 *
 * @param props - Hook configuration
 * @param props.startFrom - Optional start time (in seconds) used to seek before playback starts.
 * @param props.progressSecondInterval - Minimum delta (in seconds) between `onVideoProgress` calls. Defaults to 10.
 * @param props.onCloseVideo - Called when the player should be closed (after fullscreen handling, if needed).
 * @param props.onVideoEnd - Called when playback reaches the end (after exiting fullscreen, if needed).
 * @param props.onVideoProgress - Called with the current whole second when progress is emitted.
 * @param props.onCancelCloseVideoReady - Receives a `handleCancelExit` function that can restore fullscreen
 *   if the user cancels an exit/close flow.
 *
 * @returns Player refs and handlers:
 * - `containerRef`, `videoRef`, `reactPlayerRef`, `mediaControllerRef`
 * - `isMobileOrTablet`
 * - `handleVideoReady`, `onReactPlayerReady`, `handleCloseVideo`, `onEnded`, `onProgress`
 */
export const usePlayer = ({
  startFrom,
  progressSecondInterval = 10,
  onCloseVideo,
  onVideoEnd,
  onVideoProgress,
  onCancelCloseVideoReady,
  resetOnEnd = false,
}: UsePlayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const reactPlayerRef = useRef<HTMLVideoElement>(null);
  const mediaControllerRef = useRef<MediaControllerElement | null>(null);
  const wasFullscreenRef = useRef(false);
  const wasPausedBeforeFullscreenExitRef = useRef(false);
  const lastSecondRef = useRef(-1);
  const [playing, setPlaying] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const hideControlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isMobile, isTablet } = useBreakingPoints();
  const isMobileOrTablet = useMemo(() => isMobile || isTablet, [isMobile, isTablet]);

  const requestFullscreen = useCallback(() => {
    const video = videoRef.current;

    if (isMobileOrTablet && video) {
      const videoElement = video as HTMLVideoElement & {
        webkitSupportsFullscreen?: boolean;
        webkitEnterFullscreen?: () => void;
      };
      // iOS fullscreen support
      if (videoElement.webkitSupportsFullscreen && videoElement.webkitEnterFullscreen) {
        videoElement.webkitEnterFullscreen();
        return;
      }
      // Fallback for Android
      if (video.requestFullscreen) {
        video.requestFullscreen().catch(() => {});
      }
      return;
    }

    // On desktop, use MediaController for fullscreen
    const mediaController = mediaControllerRef.current;
    if (mediaController && !mediaController.mediaFullscreenElement) {
      mediaController.requestFullscreen?.().catch(() => {
        // Fullscreen request failed
      });
    }
  }, [isMobileOrTablet]);

  const handleVideoReady = useCallback(() => {
    // Set start time if provided (mobile/tablet)
    if (startFrom && videoRef.current) {
      videoRef.current.currentTime = startFrom;
    }
    // Autoplay video
    videoRef.current?.play().catch(() => {
      // Autoplay might be blocked by the browser
    });
  }, [startFrom]);

  const onReactPlayerReady = useCallback(() => {
    // Set start time if provided (desktop with ReactPlayer)
    if (startFrom && reactPlayerRef.current) {
      reactPlayerRef.current.currentTime = startFrom;
    }
  }, [startFrom]);

  const resetVideo = useCallback(() => {
    if (isMobileOrTablet && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.pause();
    } else if (reactPlayerRef.current) {
      reactPlayerRef.current.currentTime = 0;
      mediaControllerRef.current?.media?.pause();
    }
    setPlaying(false);
    lastSecondRef.current = -1;
  }, [isMobileOrTablet]);

  const onEnded = useCallback(() => {
    if (resetOnEnd) {
      const wasFullscreen = !!document.fullscreenElement;
      if (wasFullscreen) {
        document.exitFullscreen().then(() => resetVideo());
      } else {
        resetVideo();
      }
      return;
    }
    setPlaying(false);
    const wasFullscreen = !!document.fullscreenElement;
    if (wasFullscreen) {
      document.exitFullscreen().then(() => {
        onVideoEnd?.();
      });
    } else {
      onVideoEnd?.();
    }
  }, [onVideoEnd, resetOnEnd, resetVideo]);

  const onProgress = useCallback(
    (eventOrTime: number | SyntheticEvent<HTMLVideoElement, Event>) => {
      // ReactPlayer might pass an event (like <video>) or a number.
      const t =
        typeof eventOrTime === 'number' ? eventOrTime : eventOrTime?.currentTarget?.currentTime;

      if (typeof t !== 'number') return;

      const currentSecond = Math.floor(t);
      if (currentSecond !== lastSecondRef.current) {
        // If you seek back or reset: realign without backfilling.
        if (currentSecond < lastSecondRef.current) {
          lastSecondRef.current = currentSecond;
          onVideoProgress?.(currentSecond);
          return;
        }
        if (
          currentSecond > lastSecondRef.current &&
          currentSecond - lastSecondRef.current >= progressSecondInterval
        ) {
          lastSecondRef.current = currentSecond;
          onVideoProgress?.(currentSecond);
        }
      }
    },
    [onVideoProgress, progressSecondInterval],
  );

  const handleCloseVideo = useCallback(() => {
    setPlaying(false);
    // Pause the video
    if (isMobileOrTablet) {
      videoRef.current?.pause();
      wasFullscreenRef.current = false;
      onCloseVideo();
      return;
    }

    // On desktop, use MediaController to pause and handle fullscreen
    const mediaController = mediaControllerRef.current;
    mediaController?.media?.pause();

    const wasFullscreen = !!document.fullscreenElement;
    wasFullscreenRef.current = wasFullscreen;

    if (wasFullscreen) {
      document.exitFullscreen().then(() => {
        onCloseVideo();
      });
    } else {
      onCloseVideo();
    }
  }, [isMobileOrTablet, onCloseVideo]);

  const handleCancelExit = useCallback(() => {
    if (wasFullscreenRef.current) {
      requestFullscreen();
    }
  }, [requestFullscreen]);

  // Auto-hide controls after inactivity (only when playing)
  const resetHideControlsTimer = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }
    hideControlsTimeoutRef.current = setTimeout(() => {
      // Check if video is actually playing before hiding
      const media = mediaControllerRef.current?.media;
      const isPaused = media?.paused ?? true;
      if (!isPaused) {
        setShowControls(false);
      }
    }, 3000);
  }, []);

  const handleMouseMove = useCallback(() => {
    resetHideControlsTimer();
  }, [resetHideControlsTimer]);

  // Expose handleCancelExit to parent component
  useEffect(() => {
    onCancelCloseVideoReady?.(handleCancelExit);
  }, [onCancelCloseVideoReady, handleCancelExit]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseVideo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onCloseVideo]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, []);

  // Start the timer initially
  useEffect(() => {
    resetHideControlsTimer();
  }, [resetHideControlsTimer]);

  // Track paused state via media events and preserve it when exiting fullscreen
  useEffect(() => {
    const media = mediaControllerRef.current?.media;
    if (!media) return undefined;

    const handlePause = () => {
      wasPausedBeforeFullscreenExitRef.current = true;
    };
    const handlePlay = () => {
      wasPausedBeforeFullscreenExitRef.current = false;
    };

    media.addEventListener('pause', handlePause);
    media.addEventListener('play', handlePlay);

    return () => {
      media.removeEventListener('pause', handlePause);
      media.removeEventListener('play', handlePlay);
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isExitingFullscreen = !document.fullscreenElement;
      const media = mediaControllerRef.current?.media;

      if (isExitingFullscreen && wasPausedBeforeFullscreenExitRef.current && media) {
        // Use setTimeout to ensure pause happens after any auto-play triggered by fullscreen exit
        setTimeout(() => media.pause(), 0);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return {
    containerRef,
    videoRef,
    reactPlayerRef,
    mediaControllerRef,
    isMobileOrTablet,
    playing,
    showControls,
    handleVideoReady,
    onReactPlayerReady,
    handleCloseVideo,
    handleMouseMove,
    onEnded,
    onProgress,
  };
};
